import z from "zod";
import { AvatarSchema } from "rdx/modules/users/schemas/avatar";
import { RecommendationSummarySchema } from "rdx/modules/recommendations/schemas";
import {
  CompositionTierEnum,
  PipelineStageEnum,
  RankUpRequirementLabelEnum,
  RankUpRequirementStatusEnum,
  SubscriptionStatusEnum,
  TeamActivityTypeEnum,
} from "./enums";

export const PipelineProjectSchema = z.object({
  name: z.string(),
  value: z.string(),
  stage: z.nativeEnum(PipelineStageEnum),
  closeProb: z.number(),
  daysToClose: z.number(),
  watts: z.number().nullable(),
});

const TeamMemberBaseSchema = z.object({
  userId: z.number(),
  name: z.string(),
  initials: z.string(),
  rank: z.number(),
  rankTitle: z.string(),
  level: z.number(),
  prv90: z.string(),
  brv90: z.string(),
  daysActiveLast30: z.number(),
  nextRankProgressPct: z.number(),
  atRisk: z.boolean(),
  riskScore: z.number(),
  avatarUrls: AvatarSchema,
});

export const TeamMemberSponsorSchema = z.object({
  id: z.number(),
  fullName: z.string(),
});

export const TeamMemberListItemSchema = TeamMemberBaseSchema.extend({
  sponsor: TeamMemberSponsorSchema.optional(),
  pipelineTotal: z.string(),
  pipelineCount: z.number(),
  isAmbassador: z.boolean().optional(),
  ambassadorPayoutAmount: z.union([z.literal(1000), z.literal(2000)]).nullish(),
});

export const MetricHistoryEntrySchema = z.object({
  month: z.string(),
  prv: z.string(),
  brv: z.string(),
  daysActive: z.number(),
});

export const TeamMemberDetailAddressSchema = z.object({
  street: z.string().nullable(),
  unit: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zip: z.string().nullable(),
});

export const TeamMemberDetailSchema = TeamMemberBaseSchema.extend({
  nextRank: z.number().nullable(),
  nextRankTitle: z.string().nullable(),
  nextRankPrvThreshold: z.string().nullable(),
  nextRankBrvThreshold: z.string().nullable(),
  atRiskReason: z.string().nullable(),
  email: z.string(),
  phone: z.string().nullable(),
  address: TeamMemberDetailAddressSchema.nullish(),
  pipeline: z.array(PipelineProjectSchema),
  metricHistory: z.array(MetricHistoryEntrySchema),
});

export const TeamAveragesSchema = z.object({
  prv: z.string(),
  brv: z.string(),
  activity: z.number(),
});

export const TeamKpisSchema = z.object({
  total: z.number(),
  activeRate: z.number(),
  avgRank: z.string(),
  atRiskCount: z.number(),
  coachingQueue: z.number(),
});

export const KpiSchema = z.object({
  value: z.number(),
  prior: z.number().nullable(),
  delta: z.number().nullable(),
  sparkline: z.array(z.number()).nullable(),
});

export const TeamDashboardKpisSchema = z.object({
  teamBrv: KpiSchema,
  newInstalls: KpiSchema,
  newContracts: KpiSchema,
  newRecruits: KpiSchema,
  rankAdvancements: KpiSchema,
  totalActiveTeam: KpiSchema,
  tier1Setters: KpiSchema,
  tier2Sellers: KpiSchema,
  tier3Mentors: KpiSchema,
  enterprises: KpiSchema,
  newLeads: KpiSchema,
  newProposals: KpiSchema,
  proposalToContract: KpiSchema,
  activeSellers: KpiSchema,
  avgPrvPerActive: KpiSchema,
  orgGrowth: KpiSchema,
});

export const TeamDashboardPayloadSchema = z.object({
  kpis: TeamDashboardKpisSchema,
});

export const GetTeamDashboardResponseSchema = z.object({
  body: TeamDashboardPayloadSchema,
});

export const SellerSponsorSchema = z.object({
  id: z.number(),
  fullName: z.string(),
});

export const SellerOverviewSchema = z.object({
  prv: z.number(),
  brv: z.number(),
  activeQls: z.number(),
  newLeads: z.number(),
  proposals: z.number(),
  contracts: z.number(),
  installs: z.number(),
});

export const RankUpRequirementSchema = z.object({
  label: z.nativeEnum(RankUpRequirementLabelEnum),
  status: z.nativeEnum(RankUpRequirementStatusEnum),
  current: z.number(),
  threshold: z.number(),
  shortfall: z.number(),
});

export const RankUpEarningsSchema = z.object({
  ldbPayout: z.number(),
});

export const SellerRankUpSchema = z.object({
  nextRank: z.number(),
  requirements: z.array(RankUpRequirementSchema),
  earnings: RankUpEarningsSchema,
});

export const PipelineDealSchema = z.object({
  id: z.number(),
  homeownerName: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  stage: z.nativeEnum(PipelineStageEnum),
  daysInStage: z.number(),
  contractValue: z.number(),
});

export const SellerPipelineSchema = z.object({
  stalled: z.array(PipelineDealSchema),
  active: z.array(PipelineDealSchema),
});

export const DownlineSummarySchema = z.object({
  sellerCount: z.number(),
  r2PlusCount: z.number(),
  brv30d: z.number(),
});

export const SponseeSchema = z.object({
  id: z.number(),
  fullName: z.string(),
  currentRank: z.number(),
  lastLoginAt: z.string().nullable(),
  enrolledAt: z.string(),
  avatarUrls: AvatarSchema,
});

export const SellerTeamSchema = z.object({
  downline: DownlineSummarySchema,
  sponsees: z.array(SponseeSchema),
});

export const MemberDetailSchema = z.object({
  id: z.number(),
  fullName: z.string(),
  currentRank: z.number(),
  level: z.number(),
  sponsor: SellerSponsorSchema,
  enrolledAt: z.string(),
  avatarUrls: AvatarSchema,
  overview: SellerOverviewSchema,
  rankUp: SellerRankUpSchema,
  pipeline: SellerPipelineSchema,
  team: SellerTeamSchema,
  recommendations: z.array(RecommendationSummarySchema).optional(),
});

export const GetMemberDetailResponseSchema = z.object({
  body: MemberDetailSchema,
});

export const BrvContributionRowSchema = z.object({
  id: z.number(),
  fullName: z.string(),
  avatarUrls: AvatarSchema,
  rank: z.number(),
  level: z.number(),
  contributionPct: z.number(),
  diffPct: z.number().nullable(),
  contractsCount: z.number(),
  leadsCount: z.number(),
  proposalsCount: z.number(),
  prv: z.number(),
  previousPrv: z.number().nullable(),
});

export const TeamCompositionRowSchema = z.object({
  id: z.number(),
  fullName: z.string(),
  avatarUrls: AvatarSchema,
  level: z.number(),
  currentRank: z.number(),
  subscriptionPrice: z.number(),
  subscriptionStatus: z.nativeEnum(SubscriptionStatusEnum),
  salesCount: z.number(),
  menteeCount: z.number(),
  lastLoginAt: z.string(),
  rankAchievedAt: z.string(),
});

export const TeamCompositionSectionSchema = z.object({
  tier: z.nativeEnum(CompositionTierEnum),
  title: z.string(),
  description: z.string(),
  rows: z.array(TeamCompositionRowSchema),
});

const ProjectBaseRowSchema = z.object({
  id: z.number(),
  kw: z.number(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  ownerId: z.number(),
  ownerName: z.string(),
  ownerLevel: z.number(),
  ownerRank: z.number(),
  avatarUrls: AvatarSchema,
});

export const RecentInstallRowSchema = ProjectBaseRowSchema.extend({
  contractValue: z.number(),
  daysSinceInstall: z.number(),
});

export const ActiveContractRowSchema = ProjectBaseRowSchema.extend({
  contractValue: z.number(),
  currentStage: z.string().nullable(),
  daysInStage: z.number(),
  nextAction: z.string(),
});

export const NewRecruitRowSchema = z.object({
  id: z.number(),
  fullName: z.string(),
  avatarUrls: AvatarSchema,
  rank: z.number(),
  sponsorName: z.string(),
  enrolledAt: z.string(),
  lastLoginAt: z.string(),
});

export const RankUpRowSchema = z.object({
  id: z.number(),
  fullName: z.string(),
  avatarUrls: AvatarSchema,
  currentRank: z.number(),
  nextRank: z.number(),
  previousRank: z.number().optional(),
  rankedUpAt: z.string().optional(),
  level: z.number(),
  sponsor: SellerSponsorSchema,
  ldbEarned: z.number(),
});

export const TeamDrillPayloadSchema = z.object({
  brvContributions: z.array(BrvContributionRowSchema),
  teamComposition: z.array(TeamCompositionSectionSchema),
  recentInstalls: z.object({
    totalContractValue: z.number(),
    rows: z.array(RecentInstallRowSchema),
  }),
  activeContracts: z.object({
    stalled: z.array(ActiveContractRowSchema),
    active: z.array(ActiveContractRowSchema),
  }),
  newRecruits: z.object({
    stalled: z.array(NewRecruitRowSchema),
    onTrack: z.array(NewRecruitRowSchema),
  }),
  rankUps: z.object({
    recent: z.array(RankUpRowSchema),
    approaching: z.array(RankUpRowSchema),
  }),
});

export const GetTeamDrillResponseSchema = z.object({
  body: TeamDrillPayloadSchema,
});

export const PagingSchema = z.object({
  itemCount: z.number(),
  pageNumber: z.number(),
  pageSize: z.number(),
  hasMore: z.boolean(),
});

export const TeamMembersPayloadSchema = z.object({
  members: z.array(TeamMemberListItemSchema),
  averages: TeamAveragesSchema,
  kpis: TeamKpisSchema,
  paging: PagingSchema,
});

export const GetTeamMembersResponseSchema = z.object({
  body: TeamMembersPayloadSchema,
});

export const GetTeamMemberDetailResponseSchema = z.object({
  body: TeamMemberDetailSchema,
});

export type LineageTreeNodeT = {
  userId: number;
  fullName: string;
  avatarUrls: z.infer<typeof AvatarSchema>;
  rank: number | null;
  rankTitle: string | null;
  monthlyRevenue: string | null;
  atRisk: boolean;
  level: number;
  hasChildren: boolean;
  children: LineageTreeNodeT[];
};

export const LineageTreeNodeSchema: z.ZodType<LineageTreeNodeT> = z.lazy(() =>
  z.object({
    userId: z.number(),
    fullName: z.string(),
    avatarUrls: AvatarSchema,
    rank: z.number().nullable(),
    rankTitle: z.string().nullable(),
    monthlyRevenue: z.string().nullable(),
    atRisk: z.boolean(),
    level: z.number(),
    hasChildren: z.boolean(),
    children: z.array(LineageTreeNodeSchema),
  }),
);

export const TeamMembersFilterParamsSchema = z.object({
  searchedUserId: z.number().optional(),
  rank: z.string().optional(),
  momentum: z.string().optional(),
  status: z.string().optional(),
  memberType: z.string().optional(),
  level: z.string().optional(),
  sort: z.string().optional(),
  sortDir: z.string().optional(),
});

export const GetLineageTreeResponseSchema = z.object({
  body: z.object({
    root: LineageTreeNodeSchema,
  }),
});

export const NodeSummaryMetricsSchema = z.object({
  recruitCount: z.number(),
  contractCount: z.number(),
  installCount: z.number(),
  teamCount: z.number(),
});

export const NodeSummaryActiveProjectsSchema = z.object({
  preSalesCount: z.number(),
  postSalesCount: z.number(),
});

export const NodeSummaryActivitySchema = z.object({
  lastLeadAt: z.string().nullable(),
  lastContractAt: z.string().nullable(),
  lastRecruitAt: z.string().nullable(),
});

export const NodeSummaryInfoSchema = z.object({
  location: z.string().nullable(),
  enrolledAt: z.string().nullable(),
});

export const NodeSummaryPayloadSchema = z.object({
  userId: z.number(),
  fullName: z.string(),
  metrics: NodeSummaryMetricsSchema,
  activeProjects: NodeSummaryActiveProjectsSchema,
  activity: NodeSummaryActivitySchema,
  info: NodeSummaryInfoSchema,
});

export const GetNodeSummaryResponseSchema = z.object({
  body: NodeSummaryPayloadSchema,
});

export const TeamActivityActorSchema = z.object({
  id: z.number(),
  fullName: z.string(),
  currentRank: z.number(),
  avatarUrls: AvatarSchema,
  level: z.number(),
  sponsorName: z.string().nullable(),
  legRootId: z.number().nullish(),
  legRootName: z.string().nullish(),
});

const TeamActivityBaseSchema = z.object({
  id: z.number(),
  actor: TeamActivityActorSchema,
  createdAt: z.string(),
});

const ContractActivitySchema = TeamActivityBaseSchema.extend({
  type: z.literal(TeamActivityTypeEnum.CONTRACT),
  kw: z.number(),
  amount: z.number(),
  projectId: z.number(),
});

const ProposalActivitySchema = TeamActivityBaseSchema.extend({
  type: z.literal(TeamActivityTypeEnum.PROPOSAL),
  kw: z.number(),
  amount: z.number(),
  projectId: z.number().optional(),
});

const InstallActivitySchema = TeamActivityBaseSchema.extend({
  type: z.literal(TeamActivityTypeEnum.INSTALL),
  kw: z.number(),
  projectId: z.number(),
});

const LeadActivitySchema = TeamActivityBaseSchema.extend({
  type: z.literal(TeamActivityTypeEnum.LEAD),
  count: z.number(),
});

const RecruitActivitySchema = TeamActivityBaseSchema.extend({
  type: z.literal(TeamActivityTypeEnum.RECRUIT),
});

const RankUpActivitySchema = TeamActivityBaseSchema.extend({
  type: z.literal(TeamActivityTypeEnum.RANK_UP),
  newRank: z.number(),
  newRankTitle: z.string().optional(),
  ldbCredited: z.number().optional(),
});

export const TeamActivitySchema = z.discriminatedUnion("type", [
  ContractActivitySchema,
  ProposalActivitySchema,
  InstallActivitySchema,
  LeadActivitySchema,
  RecruitActivitySchema,
  RankUpActivitySchema,
]);

export const TeamActivityPayloadSchema = z.object({ items: z.array(TeamActivitySchema) });

export const GetTeamActivityResponseSchema = z.object({ body: TeamActivityPayloadSchema });
